import styled from 'styled-components';
import { space, color, typography, flexbox, variant, layout } from 'styled-system';
import { StyledProps } from 'types/styles';
import { themeGet } from '@styled-system/theme-get';
import Select from 'react-select';

interface Props extends StyledProps {
  variant?: string;
  hideIndicator?: boolean;
  smallPadding?: boolean;
  height?: number;
  error?: boolean;
}

export const SelectWrapper = styled(Select)<Props>(
  {
    appearance: 'none',
    fontFamily: 'inherit',
    display: 'flex',
    fontSize: '14px',
    '&.styled-select--is-disabled': {
      background: '#F5F5F5',
    },
    '.styled-select__single-value': {
      display: 'flex',
      alignItems: 'center',
      svg: {
        marginRight: '6px',
        height: '14px',
        width: '14px',
      },
    },
    '.styled-select__control': {
      width: '100%',
      borderColor: 'transparent',
      borderTop: 'none',
      borderLeft: 'none',
      borderRight: 'none',
      borderRadius: '8px',
      background: 'white',
      alignContent: 'center',
      overflowY: 'auto',
      '&:hover': {
        borderColor: 'transparent',
      },
    },

    '.styled-select__indicator-separator': {
      display: 'none',
    },
    '.styled-select__placeholder': {
      color: '#bfbfbf',
    },
    '.styled-select__loading-indicator': {
      display: 'none',
    },
    '.styled-select__value-container': {
      paddingLeft: '0',
      paddingRight: '0',
      display: 'flex',
      fontSize: 14,
      paddingTop: '1px',
      paddingBottom: '1px',
    },
    '.styled-select__value-container--is-multi': {
      '> div': {
        width: 'max-content !important',
      },
      '.styled-select__multi-value': {
        backgroundColor: themeGet('colors.glassDark.0'),
        borderRadius: '20px',
        '.styled-select__multi-value__label': {
          fontSize: '12px',
          color: themeGet('colors.glassDark.8'),
          paddingTop: '0px',
          paddingBottom: '0px',
          paddingLeft: '10px',
          paddingRight: '6px',
        },
      },
    },
    '.styled-select__multi-value__remove': {
      paddingLeft: '2px',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.05)',
        color: 'currentColor',
        borderTopRightRadius: '20px',
        borderBottomRightRadius: '20px',
      },
    },
    svg: {
      width: '16px',
      height: 'auto',
    },
  },
  space,
  layout,
  color,
  flexbox,
  typography,
  variant({
    variants: {
      primary: {
        '.styled-select__control': {
          border: '1px solid',
          borderColor: 'grays.3',
          px: 3,
          boxShadow: 'none',
          '&:hover': {
            borderColor: 'grays.3',
          },
        },
      },
      secondary: {
        '.styled-select__control': {
          width: '100%',
          bg: 'transparent',
          boxShadow: 'none',
          border: '1px solid',
          borderColor: themeGet('colors.glassDark.2'),
          borderRadius: themeGet('radii.4'),
          px: 3,
          '.styled-select__indicator': {
            display: 'none',
          },
        },
        '&:hover': {
          transition: 'all 0.2s ease-in-out 0s',
        },
        '&:focus': {
          transition: 'all 0.2s ease-in-out 0s',
        },
      },
      underline: {
        '.styled-select__control': {
          width: '100%',
          boxShadow: 'none',
          borderColor: 'transparent',
          borderTop: 'none',
          borderLeft: 'none',
          borderRight: 'none',
          borderRadius: 0,
          '&:hover': {
            borderColor: 'greens.2',
          },
        },
        '.styled-select__control--is-focused': {
          borderColor: 'transparent',
          borderBottom: 'none',
        },
        borderBottom: '1px solid',
        borderBottomColor: 'transparent',
        '&:hover': {
          transition: 'all 0.2s ease-in-out 0s',
        },
        '&:focus': {
          transition: 'all 0.2s ease-in-out 0s',
        },
      },

      outline: {
        border: '1px solid',
        borderColor: 'grays.3',
        px: 3,
        '&:hover': {
          borderColor: 'greens.2',
          transition: 'all 0.2s ease-in-out 0s',
        },
        '&:focus': {
          borderColor: 'greens.2',
          boxShadow: 'inputInset',
          transition: 'all 0.2s ease-in-out 0s',
        },
        '&:disabled': {
          backgroundColor: 'grays.2',
        },
      },
    },
  }),
  ({ error, hideIndicator }) => {
    return {
      '.styled-select__control': {
        borderColor: error ? '#90342A' : '#e0e0e0',
        transition: 'border-color 0.25s ease-in-out 0s',
        '&:hover': {
          borderColor: error ? '#90342A' : '#e0e0e0',
        },
      },
      '.styled-select__indicators': {
        display: hideIndicator ? 'none' : 'block',
      },
      '.styled-select__placeholder': {
        color: error ? '#90342A' : '#bfbfbf',
      },
    };
  },
);

SelectWrapper.defaultProps = {
  px: 0,
  minHeight: '36px',
  fontWeight: 2,
  alignItems: 'center',
  justifyContent: 'center',
};

export const LabelWrapper = styled.label<Props>`
  ${color}
  ${typography}
  ${space}
`;

export const FieldWrapper = styled.div<Props>`
  ${space}
  ${layout}
  ${flexbox}
`;

export const DescriptionWrapper = styled.div<Props>`
  ${color}
  ${typography}
  ${space}
  ${layout}
`;

export const OptionalWrapper = styled.div<Props>`
  ${color}
  ${typography}
  ${space}
  ${layout}
  white-space: nowrap;
`;

OptionalWrapper.defaultProps = {
  color: 'text.primary',
  fontSize: 1,
};
